import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { AlertCircle, Building2, CheckCircle2, Clock3, UserPlus } from 'lucide-react';

const PHONE_REGEX = /^\d{10}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const AADHAAR_REGEX = /^\d{12}$/;
const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]$/;

function formatExpiry(expiresAt) {
  if (!expiresAt) {
    return '';
  }

  return new Date(expiresAt).toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
}

export default function PublicTenantQrPage() {
  const { inviteId } = useParams();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    idProofType: 'Aadhaar',
    idProofNumber: '',
    emergencyContact: '',
    joinDate: new Date().toISOString().split('T')[0],
    monthlyRent: '',
    depositAmount: ''
  });

  useEffect(() => {
    const fetchInvite = async () => {
      setLoading(true);
      setErrorMessage('');
      try {
        const response = await api.getTenantQrInvite(inviteId);
        const inviteData = response.data;
        setInvite(inviteData);
        setFormData((current) => ({
          ...current,
          monthlyRent: inviteData.suggestedRent ?? '',
          depositAmount: inviteData.suggestedDeposit ?? ''
        }));
      } catch (error) {
        setErrorMessage(error.message || 'This QR invite is invalid or has expired.');
      } finally {
        setLoading(false);
      }
    };

    fetchInvite();
  }, [inviteId]);

  const expiryLabel = useMemo(() => formatExpiry(invite?.expiresAt), [invite?.expiresAt]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    const trimmedName = formData.name.trim();
    const trimmedPhone = formData.phone.trim();
    const trimmedEmail = formData.email.trim();
    const trimmedEmergencyContact = formData.emergencyContact.trim();
    const trimmedIdProofType = formData.idProofType.trim();
    const trimmedIdProofNumber = formData.idProofNumber.trim();

    if (!PHONE_REGEX.test(trimmedPhone)) {
      toast.error('Phone number must be exactly 10 digits');
      return;
    }

    if (!PHONE_REGEX.test(trimmedEmergencyContact)) {
      toast.error('Emergency contact must be exactly 10 digits');
      return;
    }

    if (trimmedEmail && !EMAIL_REGEX.test(trimmedEmail)) {
      toast.error('Enter a valid email address');
      return;
    }

    if (trimmedIdProofType === 'Aadhaar' && !AADHAAR_REGEX.test(trimmedIdProofNumber)) {
      toast.error('Aadhaar number must be exactly 12 digits');
      return;
    }

    if (trimmedIdProofType === 'PAN Card' && !PAN_REGEX.test(trimmedIdProofNumber.toUpperCase())) {
      toast.error('PAN number must be in valid PAN format');
      return;
    }

    setSubmitting(true);
    try {
      await api.submitTenantQrInvite(inviteId, {
        name: trimmedName,
        phone: trimmedPhone,
        email: trimmedEmail || null,
        idProofType: trimmedIdProofType,
        idProofNumber: trimmedIdProofType === 'PAN Card' ? trimmedIdProofNumber.toUpperCase() : trimmedIdProofNumber,
        emergencyContact: trimmedEmergencyContact,
        joinDate: formData.joinDate,
        monthlyRent: Number(formData.monthlyRent),
        depositAmount: Number(formData.depositAmount)
      });
      setSubmitted(true);
      toast.success('Your details were submitted successfully');
    } catch (error) {
      toast.error(error.message || 'Unable to submit your details');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 px-4 py-10">
        <div className="mx-auto flex max-w-xl items-center justify-center rounded-3xl bg-white p-10 shadow-sm">
          <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-100 px-4 py-10">
        <div className="mx-auto max-w-xl rounded-3xl bg-white p-8 shadow-sm">
          <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
            <CheckCircle2 className="h-8 w-8 text-green-600" />
          </div>
          <h1 className="text-center text-2xl font-bold text-slate-900 font-heading">Request Submitted</h1>
          <p className="mt-3 text-center text-slate-600">
            Your tenant details were shared with the PG owner. This QR is now closed and cannot be reused.
          </p>
        </div>
      </div>
    );
  }

  if (errorMessage || !invite) {
    return (
      <div className="min-h-screen bg-slate-100 px-4 py-10">
        <div className="mx-auto max-w-xl rounded-3xl bg-white p-8 shadow-sm">
          <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
            <AlertCircle className="h-8 w-8 text-red-600" />
          </div>
          <h1 className="text-center text-2xl font-bold text-slate-900 font-heading">QR Not Available</h1>
          <p className="mt-3 text-center text-slate-600">
            {errorMessage || 'This QR invite is invalid or has expired.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 px-4 py-6 sm:py-10">
      <div className="mx-auto max-w-3xl space-y-6">
        <div className="rounded-3xl bg-white p-6 shadow-sm sm:p-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10">
                <UserPlus className="h-6 w-6 text-primary" />
              </div>
              <h1 className="break-words text-2xl font-bold text-slate-900 font-heading sm:text-3xl">
                Tenant Self Onboarding
              </h1>
              <p className="mt-2 text-slate-600">
                Fill in your details for {invite.pgName}, {invite.floorName} - Room {invite.roomNumber} - Bed {invite.bedNumber}
              </p>
            </div>
            <div className="rounded-2xl bg-amber-50 px-4 py-3 text-sm text-amber-800">
              <div className="flex items-center gap-2 font-semibold">
                <Clock3 className="h-4 w-4" />
                Expires at {expiryLabel}
              </div>
            </div>
          </div>

          <div className="mt-6 grid gap-4 md:grid-cols-3">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="mb-2 flex items-center gap-2 text-slate-600">
                <Building2 className="h-4 w-4" />
                Property
              </div>
              <p className="font-semibold text-slate-900">{invite.pgName}</p>
              <p className="mt-1 text-sm text-slate-500">{invite.pgAddress}</p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-sm text-slate-600">Assigned bed</p>
              <p className="mt-2 font-semibold text-slate-900">{invite.floorName}</p>
              <p className="text-sm text-slate-500">Room {invite.roomNumber} - Bed {invite.bedNumber}</p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-sm text-slate-600">Suggested charges</p>
              <p className="mt-2 font-semibold text-slate-900">Rent: ₹{Number(invite.suggestedRent || 0).toLocaleString('en-IN')}</p>
              <p className="text-sm text-slate-500">Deposit: ₹{Number(invite.suggestedDeposit || 0).toLocaleString('en-IN')}</p>
            </div>
          </div>
        </div>

        <div className="rounded-3xl bg-white p-6 shadow-sm sm:p-8">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <Label className="mb-1.5 block text-sm font-semibold text-slate-700">Full Name *</Label>
                <Input
                  placeholder="Rahul Sharma"
                  value={formData.name}
                  onChange={(event) => setFormData({ ...formData, name: event.target.value })}
                  className="h-12 rounded-lg"
                  required
                />
              </div>
              <div>
                <Label className="mb-1.5 block text-sm font-semibold text-slate-700">Phone Number *</Label>
                <Input
                  placeholder="9876543210"
                  value={formData.phone}
                  onChange={(event) => {
                    const value = event.target.value.replace(/\D/g, '').slice(0, 10);
                    setFormData({ ...formData, phone: value });
                  }}
                  inputMode="numeric"
                  maxLength={10}
                  className="h-12 rounded-lg"
                  required
                />
              </div>
            </div>

            <div>
              <Label className="mb-1.5 block text-sm font-semibold text-slate-700">Email (Optional)</Label>
              <Input
                type="email"
                placeholder="rahul@example.com"
                value={formData.email}
                onChange={(event) => setFormData({ ...formData, email: event.target.value })}
                className="h-12 rounded-lg"
              />
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <Label className="mb-1.5 block text-sm font-semibold text-slate-700">ID Proof Type *</Label>
                <select
                  value={formData.idProofType}
                  onChange={(event) => setFormData({ ...formData, idProofType: event.target.value, idProofNumber: '' })}
                  className="h-12 w-full rounded-lg border border-slate-300 bg-white px-4 focus:ring-2 focus:ring-primary/20"
                >
                  <option>Aadhaar</option>
                  <option>PAN Card</option>
                  <option>Driving License</option>
                  <option>Passport</option>
                </select>
              </div>
              <div>
                <Label className="mb-1.5 block text-sm font-semibold text-slate-700">ID Proof Number *</Label>
                <Input
                  placeholder={formData.idProofType === 'Aadhaar' ? '123412341234' : formData.idProofType === 'PAN Card' ? 'ABCDE1234F' : 'Enter ID number'}
                  value={formData.idProofNumber}
                  onChange={(event) => {
                    let value = event.target.value;
                    if (formData.idProofType === 'Aadhaar') {
                      value = value.replace(/\D/g, '').slice(0, 12);
                    } else if (formData.idProofType === 'PAN Card') {
                      value = value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10);
                    }
                    setFormData({ ...formData, idProofNumber: value });
                  }}
                  inputMode={formData.idProofType === 'Aadhaar' ? 'numeric' : undefined}
                  maxLength={formData.idProofType === 'Aadhaar' ? 12 : formData.idProofType === 'PAN Card' ? 10 : undefined}
                  className="h-12 rounded-lg"
                  required
                />
              </div>
            </div>

            <div>
              <Label className="mb-1.5 block text-sm font-semibold text-slate-700">Emergency Contact *</Label>
              <Input
                placeholder="9123456789"
                value={formData.emergencyContact}
                onChange={(event) => {
                  const value = event.target.value.replace(/\D/g, '').slice(0, 10);
                  setFormData({ ...formData, emergencyContact: value });
                }}
                inputMode="numeric"
                maxLength={10}
                className="h-12 rounded-lg"
                required
              />
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div>
                <Label className="mb-1.5 block text-sm font-semibold text-slate-700">Join Date *</Label>
                <Input
                  type="date"
                  value={formData.joinDate}
                  onChange={(event) => setFormData({ ...formData, joinDate: event.target.value })}
                  className="h-12 rounded-lg"
                  required
                />
              </div>
              <div>
                <Label className="mb-1.5 block text-sm font-semibold text-slate-700">Monthly Rent (₹) *</Label>
                <Input
                  type="number"
                  placeholder="5000"
                  value={formData.monthlyRent}
                  onChange={(event) => setFormData({ ...formData, monthlyRent: event.target.value })}
                  className="h-12 rounded-lg"
                  required
                />
              </div>
              <div>
                <Label className="mb-1.5 block text-sm font-semibold text-slate-700">Deposit (₹) *</Label>
                <Input
                  type="number"
                  placeholder="10000"
                  value={formData.depositAmount}
                  onChange={(event) => setFormData({ ...formData, depositAmount: event.target.value })}
                  className="h-12 rounded-lg"
                  required
                />
              </div>
            </div>

            <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
              This link works without login and closes automatically after 30 minutes or after the first successful submission.
            </div>

            <Button
              type="submit"
              className="h-12 w-full rounded-xl bg-primary font-semibold text-white hover:bg-primary/90"
              disabled={submitting}
            >
              {submitting ? 'Submitting...' : 'Submit My Details'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
